import { Button, Modal } from '@orbita-ui/core';
import { Col, Container, Row } from 'react-bootstrap';
import { Card } from 'shared/components';
import { PageTitle } from 'shared/components/Fonts';
import { useNavigate } from 'react-router-dom';
import { useContext, useState } from 'react';
import { COST_CENTRE_CREATE_PATH } from 'core/centrocosto/routes';
import { BaseModal, useModal } from 'shared/modal';
import { CostCentreTableWithPagination } from 'core/centrocosto/components/CostCentreTableWithPagination/CostCentreTableWithPagination';
import { FilterCostCentreForm } from 'core/centrocosto/components/FilterCostCentreForm/FilterCostCentreForm';
import { CostCentresListContext } from 'core/centrocosto/contexts/CostCentreListContext';
import { useCoreService } from 'core/services/useCoreService';
import { FetchDataError } from 'shared/http';
import { CentroCosto } from 'core/centrocosto/models/CentroCosto';
import { useNotifications } from 'shared/notifications';
import { useImpersonationGuard } from 'auth/hooks/useImpersonationGuard';

const DELETE_CCOST_CENTRE_ERROR_MESSAGES = {
  account_with_balance: (centroCosto: CentroCosto) =>
    `El centro de costos ${centroCosto.nombre} no se puede eliminar porque la cuenta asociada tiene saldo disponible mayor a cero.`,
  account_with_children: (centroCosto: CentroCosto) =>
    `El centro de costos ${centroCosto.nombre} no se puede eliminar porque tiene centros de costos o tarjetas que dependen de éste.`,
  default: 'Hubo un error al eliminar el centro de costos',
};

export function CostCentreListPage() {
  const { searchParams, reloadToken, reloadCostCentresAndFilters } =
    useContext(CostCentresListContext);

  const navigate = useNavigate();
  const coreService = useCoreService();

  // Modal
  const { showModal, openModal, closeModal } = useModal(false);
  const [modalMessage, setModalMessage] = useState<string>('');
  const [selectedCostCentre, setSelectedCostCentre] = useState<CentroCosto>(null);
  const [isConfirmDelete, setIsConfirmDelete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { addNotification } = useNotifications();
  const { guardWriteAction } = useImpersonationGuard();

  const startDeleteFlow = (centroCosto: CentroCosto) => {
    if (!guardWriteAction()) {
      return;
    }
    setSelectedCostCentre(centroCosto);
    setModalMessage('¿Desea eliminar el centro de costos seleccionado?');
    setIsConfirmDelete(true);
    openModal();
  };

  const confirmDelete = async () => {
    if (!selectedCostCentre) return;
    if (!guardWriteAction()) {
      return;
    }

    setIsLoading(true);
    try {
      await coreService.deleteCostCentre(selectedCostCentre.id);
      closeModal();
      reloadCostCentresAndFilters();
      addNotification('El centro de costos fue eliminado correctamente', 'success');
    } catch (error: any) {
      const msg =
        DELETE_CCOST_CENTRE_ERROR_MESSAGES[(error as FetchDataError).apiError?.message];
      setModalMessage(
        msg ? msg(selectedCostCentre) : DELETE_CCOST_CENTRE_ERROR_MESSAGES.default,
      );
      setIsConfirmDelete(false);
      openModal();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal show={showModal} onClose={closeModal}>
        <BaseModal
          title={'Eliminar Centro de Costos'}
          body={<p>{modalMessage}</p>}
          footer={
            <>
              <Button
                className="bg-secondary"
                color="inverted"
                variant="ghost"
                onClick={closeModal}
              >
                Cerrar
              </Button>

              {isConfirmDelete && (
                <Button color="destructive" disabled={isLoading} onClick={confirmDelete}>
                  Eliminar
                </Button>
              )}
            </>
          }
        />
      </Modal>

      <Container fluid>
        <Row className="mb-3 d-flex justify-content-between align-items-center">
          <Col>
            <PageTitle>Centros de costos</PageTitle>
          </Col>
          <Col className="text-end">
            <Button
              color="primary"
              icon="AddIcon"
              type="button"
              onClick={() => guardWriteAction(() => navigate(COST_CENTRE_CREATE_PATH))}
            >
              Crear Centro de Costo
            </Button>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Title>Filtros</Card.Title>
              <Card.Body>
                <FilterCostCentreForm />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <Card>
              <Card.Title>Centros de Costos</Card.Title>
              <Card.Body>
                <CostCentreTableWithPagination
                  searchParams={searchParams}
                  reloadToken={reloadToken}
                  onDelete={startDeleteFlow}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
